import { CheckIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { ReactNode } from 'react'
import { formatNumericValue } from 'utils/numbers'
import Modal from '../shared/Modal'
import { ModalProps } from 'types/modal'
import { LinkButton } from '../shared/Button'
import Link from 'next/link'
import { useMintInfo } from 'hooks/useMintInfo'
import TokenGenesis from './TokenGenesis'
import store from 'stores/store'
import { useTranslation } from 'next-i18next'
import { ExternalLink } from '@components/shared/ExternalLink'
import { clsx } from 'clsx'
import { TokenAddress } from '@components/shared/TokenAddress'

const TokenDetails = () => {
  const { t } = useTranslation('token-page')
  const baseToken = store((s) => s.market.baseToken)
  const { mintInfoData } = useMintInfo(baseToken.mint, baseToken.token_program)
  const { mintInfo, metadata } = mintInfoData ?? {}

  return (
    <div className="h-full">
      <div className="shared-container h-full p-4 md:p-6">
        <h3 className="mb-4">{t('details.title')}</h3>
        <div className="flex flex-col items-center">
          <div className="w-full space-y-3">
            {baseToken ? (
              <KeyValuePair
                title={t('details.address')}
                value={
                  <TokenAddress
                    className="flex items-center"
                    token={baseToken}
                  />
                }
              />
            ) : null}
            <KeyValuePair
              title={t('details.max-supply')}
              value={
                mintInfo?.supply ? formatNumericValue(mintInfo.supply) : '–'
              }
              isNumber
            />
            <KeyValuePair
              title={t('details.mint-authority')}
              value={
                mintInfo?.mintAuthority
                  ? t('details.enabled')
                  : t('details.disabled')
              }
              valueClassName={
                mintInfo?.mintAuthority ? 'text-th-down' : 'text-th-up'
              }
              prefix={
                mintInfo?.mintAuthority ? (
                  <XMarkIcon className="size-4" />
                ) : (
                  <CheckIcon className="size-4" />
                )
              }
            />
            <KeyValuePair
              title={t('details.freeze-authority')}
              value={
                mintInfo?.freezeAuthority
                  ? t('details.enabled')
                  : t('details.disabled')
              }
              valueClassName={
                mintInfo?.freezeAuthority ? 'text-th-down' : 'text-th-up'
              }
              prefix={
                mintInfo?.freezeAuthority ? (
                  <XMarkIcon className="size-4" />
                ) : (
                  <CheckIcon className="size-4" />
                )
              }
            />
            <KeyValuePair
              title={t('details.metadata-locked')}
              value={
                metadata?.metadata?.updateAuthority
                  ? t('details.disabled')
                  : t('details.enabled')
              }
            />
            <TokenGenesis />
          </div>
        </div>
      </div>
    </div>
  )
}

export default TokenDetails

export const KeyValuePair = ({
  title,
  value,
  isNumber,
  prefix,
  suffix,
  onClick,
  linkPath,
  valueClassName = '',
}: {
  title: string | ReactNode
  value: string | ReactNode
  isNumber?: boolean
  prefix?: ReactNode
  suffix?: ReactNode
  onClick?: () => void
  linkPath?: string
  valueClassName?: string
}) => {
  const LinkCmp = linkPath && linkPath.includes('https') ? ExternalLink : Link

  return (
    <div className="flex items-start justify-between">
      <span className="text-th-fgd-3">{title}</span>
      {linkPath ? (
        <LinkCmp className="text-th-fgd-1" href={linkPath}>
          <span
            className={clsx(
              'flex items-center',
              isNumber && 'font-mono',
              valueClassName,
            )}
          >
            {prefix ? <div className="mr-1">{prefix}</div> : null}
            <span>{value}</span>
            {suffix ? <div className="ml-1">{suffix}</div> : null}
          </span>
        </LinkCmp>
      ) : (
        <div
          className={clsx(
            'text-th-fgd-1',
            onClick &&
              'default-transition cursor-pointer md:hover:text-th-fgd-3',
          )}
          onClick={onClick ? onClick : undefined}
        >
          <span
            className={clsx(
              'flex items-center',
              isNumber && 'font-mono',
              valueClassName,
            )}
          >
            {prefix ? <div className="mr-1">{prefix}</div> : null}
            <span>{value}</span>
            {suffix ? <div className="ml-1">{suffix}</div> : null}
          </span>
        </div>
      )}
    </div>
  )
}

interface DescriptionModalProps {
  desc: string
}

type ModalCombinedProps = DescriptionModalProps & ModalProps

export const DescriptionModal = ({
  isOpen,
  onClose,
  desc,
}: ModalCombinedProps) => {
  const { t } = useTranslation('token-page')
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <p className="mb-4">{desc}</p>
      <LinkButton onClick={onClose}>{t('info.close')}</LinkButton>
    </Modal>
  )
}
